import { useState } from 'react';

import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  Separator,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { redFlagsSchema } from '@eluve/llm-outputs';

// Private Components

interface RedFlagCardProps {
  callout: string;
  context: string;
  onFeedback?: (isHelpful: boolean, redFlag: string) => void;
}

const RedFlagCard: React.FC<RedFlagCardProps> = ({
  callout,
  context,
  onFeedback,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<boolean>(false);
  const [isHelpful, setIsHelpful] = useState<boolean>(false);

  const feedbackHandler = (isHelpful: boolean) => {
    setFeedbackGiven(true);
    setIsHelpful(isHelpful);
    onFeedback?.(isHelpful, callout);
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full pr-3 pl-4"
    >
      <CollapsibleTrigger className="w-full p-2">
        <Stack justify="between" gap={2} direction="horizontal">
          <Text color="secondary">{callout}</Text>
          <Icon
            name="ChevronDown"
            size="lg"
            className='text-text-supporting transition-transform duration-300 ease-in-out data-[state="open"]:-rotate-180'
            data-state={isOpen ? 'open' : 'closed'}
          />
        </Stack>
      </CollapsibleTrigger>
      <CollapsibleContent className="p-2">
        <Stack className="rounded-xl bg-bg-secondary p-4" gap={2}>
          <Text weight="regular" className="text-text-gray">
            {context}
          </Text>
          <Separator className="my-2 bg-border-secondary" />
          <Stack justify="between" gap={2} direction="horizontal">
            <Text inline color="tertiary" size="xs">
              {t('appointmentPage.redFlags.wasHelpful')}
            </Text>
            <Stack className="w-fit" gap={2} direction="horizontal">
              <Button
                variant={
                  feedbackGiven && isHelpful === false ? 'ghost' : 'outline'
                }
                size="sm"
                disabled={feedbackGiven}
                onClick={() => feedbackHandler(true)}
              >
                {t('appointment.feedback.yes')}
              </Button>
              <Button
                variant={
                  feedbackGiven && isHelpful === true ? 'ghost' : 'outline'
                }
                size="sm"
                disabled={feedbackGiven}
                onClick={() => feedbackHandler(false)}
              >
                {t('appointment.feedback.no')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Public Components

export interface RedFlagsOutputProps {
  content: Record<string, unknown> | Record<string, unknown>[];
  onFeedback: (isHelpful: boolean, redFlag: string) => void;
  onShowDiagnoses: () => void;
}

export const RedFlagsOutput: React.FC<RedFlagsOutputProps> = ({
  content,
  onFeedback,
  onShowDiagnoses,
}) => {
  const { t } = useTranslation();
  const redFlags = redFlagsSchema.safeParse(content);

  if (!redFlags.success) {
    return null;
  }

  if (redFlags.data.length === 0) {
    return (
      <Stack className="px-6 py-2" gap={2}>
        <Text weight="semibold">{t('appointmentPage.redFlags.noneTitle')}</Text>
        <Text weight="regular" className="mb-3 text-text-accent-secondary">
          {t('appointmentPage.redFlags.noneDescription')}
        </Text>
        <Button variant="outline" size="sm" onClick={onShowDiagnoses}>
          {t('appointmentPage.redFlags.viewDdx')}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      {redFlags.data.map((redFlag) => (
        <RedFlagCard
          key={redFlag.callout}
          callout={redFlag.callout}
          context={redFlag.context}
          onFeedback={onFeedback}
        />
      ))}
    </Stack>
  );
};
